import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { MODAL_ROUTES } from '@woolworthsnz/shop';
import {
	BreakPointService,
	ButtonState,
	ButtonComponent,
	CustomWindow,
	DatalayerService,
	EmbeddedVisibilityDirective,
	Device,
	ModalRoutingService,
	NotificationType,
	SvgIconComponent,
	WINDOW,
} from '@woolworthsnz/styleguide';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RECIPE_SHARE_MODAL_PATH } from '../share-modal/share-modal.component';
import { NutritionalInfoComponent } from '../nutritional-info/nutritional-info.component';
import { PrepSummaryComponent } from '../prep-summary/prep-summary.component';
import { MealTimesComponent } from '../meal-times/meal-times.component';
import { NgIf, NgClass, AsyncPipe } from '@angular/common';

@Component({
	selector: 'recipe-details-header',
	templateUrl: './details-header.component.html',
	styleUrls: ['./details-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, MealTimesComponent, PrepSummaryComponent, NgClass, NutritionalInfoComponent, SvgIconComponent, ButtonComponent, EmbeddedVisibilityDirective, AsyncPipe]
})
export class DetailsHeaderComponent implements AfterViewInit {
	@Input() id: number;
	@Input() title = '';
	@Input() description = '';
	@Input() mealTimes: string[] = [];
	@Input() imageUrl = '';
	@Input() prepDuration?: number;
	@Input() cookDuration?: number;
	@Input() servings?: string;
	@Input() saveButtonState: ButtonState = ButtonState.default;
	@Input() saved = false;
	@Input() nutritionTags: string;
	@Input() isMealPlanningEnabled = false;
	/** isMealPlanningContext defines whether page was accessed from a mealPlanning page and shows a slightly different layout based on this */
	@Input() isMealPlanningContext: boolean;
	@Input() isAddedToMealPlan: boolean;
	@Input() isEmbeddedInApp: boolean;

	@Output() save = new EventEmitter<void>();
	@Output() addToMealPlan = new EventEmitter<void>();
	@Output() viewMealPlan = new EventEmitter<void>();

	@ViewChild('descriptionEl') descriptionEl: ElementRef;

	collapsed = true;
	isDesktop$: Observable<boolean>;

	constructor(
		private modalRoutingService: ModalRoutingService,
		@Inject(WINDOW) private window: CustomWindow,
		private breakpointService: BreakPointService,
		private cdr: ChangeDetectorRef,
		private datalayerService: DatalayerService
	) {
		this.isDesktop$ = this.breakpointService.device$.pipe(
			map((device) => device === Device.DESKTOP || device === Device.LARGE || device === Device.XLARGE)
		);
	}

	ngAfterViewInit(): void {
		if (this.descriptionEl) {
			// Needs to be on next tick so that the content is rendered
			setTimeout(() => {
				this.collapsed = this.descriptionEl.nativeElement.offsetHeight < this.descriptionEl.nativeElement.scrollHeight;
				this.cdr.markForCheck();
			});
		}
	}

	expand(): void {
		this.collapsed = false;
	}

	share(): void {
		this.datalayerService.trackRecipeEvent(NotificationType.Button, 'RDP_share', this.id);
		// use native share if available
		if (navigator?.share) {
			navigator.share({
				title: this.title,
				url: this.window?.location?.href,
			});
			return;
		}

		// open share dialog
		this.modalRoutingService.openWithOptions(
			MODAL_ROUTES.RECIPE_MODAL,
			{ skipLocationChange: true },
			RECIPE_SHARE_MODAL_PATH
		);
	}

	public print(): void {
		this.datalayerService.trackRecipeEvent(NotificationType.Button, 'RDP_print', this.id);
		this.window.print();
	}

	public addToMealPlanClick(): void {
		this.addToMealPlan.emit();
	}

	public viewMealPlanClick(): void {
		this.viewMealPlan.emit();
	}
}
